import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/shared/ui';

import styles from './TabActionBar.module.scss';

export type TabActionBarKind =
  | 'document-approvable'
  | 'document-readonly'
  | 'analytics'
  | 'transcript';

type TabActionBarProps = {
  kind: TabActionBarKind;
  contextLabel: string;
  className?: string;
  isApproved?: boolean;
  onApprove?: () => void;
  onEdit?: () => void;
  onCopy?: () => void;
  onRefresh?: () => void;
  onOpenDocument?: () => void;
};

const logStub = (action: string, context: string) => () =>
  console.log(`[EncounterDetails] ${action} on ${context} — TODO (stage 10)`);

export const TabActionBar: FC<TabActionBarProps> = (props) => {
  const {
    kind,
    contextLabel,
    className,
    isApproved = false,
    onApprove,
    onEdit,
    onCopy,
    onRefresh,
    onOpenDocument,
  } = props;
  const { formatMessage } = useIntl();

  const handleApprove = onApprove ?? logStub('approve', contextLabel);
  const handleEdit = onEdit ?? logStub('edit', contextLabel);
  const handleCopy = onCopy ?? logStub('copy', contextLabel);
  const handleRefresh = onRefresh ?? logStub('refresh', contextLabel);
  const handleOpenDocument =
    onOpenDocument ?? logStub('open-document', contextLabel);

  if (kind === 'document-approvable') {
    return (
      <div
        data-component="encounter-actions"
        className={cn(
          styles.fullBar,
          isApproved ? styles.fullBarApproved : styles.fullBarNotApproved,
          className,
        )}
      >
        <span className={cn('p2', styles.fullBarLabel)}>
          {isApproved ? (
            <FormattedMessage
              id="encounters.details.approved"
              defaultMessage="Approved"
            />
          ) : (
            <FormattedMessage
              id="encounters.details.notApproved"
              defaultMessage="Not approved"
            />
          )}
        </span>

        <div className={styles.fullBarButtons}>
          <Button
            variant="primary"
            success={!isApproved}
            size="small"
            onClick={handleApprove}
          >
            {isApproved ? (
              <FormattedMessage
                id="encounters.details.unapprove"
                defaultMessage="Unapprove"
              />
            ) : (
              <FormattedMessage
                id="encounters.details.approve"
                defaultMessage="Approve"
              />
            )}
          </Button>
          <Button
            variant="tertiary"
            size="small"
            icon="pen"
            aria-label={formatMessage({
              id: 'global.edit',
              defaultMessage: 'Edit',
            })}
            onClick={handleEdit}
          />
          <Button
            variant="tertiary"
            size="small"
            icon="copy"
            aria-label={formatMessage({
              id: 'global.copy',
              defaultMessage: 'Copy',
            })}
            onClick={handleCopy}
          />
          <Button
            variant="tertiary"
            size="small"
            icon="rotate"
            aria-label={formatMessage({
              id: 'encounters.details.refresh',
              defaultMessage: 'Refresh',
            })}
            onClick={handleRefresh}
          />
          <Button
            variant="tertiary"
            size="small"
            icon="doc"
            aria-label={formatMessage({
              id: 'encounters.details.openDocument',
              defaultMessage: 'Open document',
            })}
            onClick={handleOpenDocument}
          />
        </div>
      </div>
    );
  }

  if (kind === 'document-readonly') {
    return (
      <div className={cn(styles.iconRow, className)}>
        <Button
          variant="tertiary"
          size="small"
          icon="copy"
          aria-label={formatMessage({
            id: 'global.copy',
            defaultMessage: 'Copy',
          })}
          onClick={handleCopy}
        />
        <Button
          variant="tertiary"
          size="small"
          icon="rotate"
          aria-label={formatMessage({
            id: 'encounters.details.refresh',
            defaultMessage: 'Refresh',
          })}
          onClick={handleRefresh}
        />
        <Button
          variant="tertiary"
          size="small"
          icon="doc"
          aria-label={formatMessage({
            id: 'encounters.details.openDocument',
            defaultMessage: 'Open document',
          })}
          onClick={handleOpenDocument}
        />
      </div>
    );
  }

  // analytics / transcript — only refresh
  return (
    <div className={cn(styles.iconRow, className)}>
      <Button
        variant="tertiary"
        size="small"
        icon="rotate"
        aria-label={formatMessage({
          id: 'encounters.details.refresh',
          defaultMessage: 'Refresh',
        })}
        onClick={handleRefresh}
      />
    </div>
  );
};
